if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          controls: this.querySelector('.gallery-viewer-controls'),
          dots: this.querySelector('[data-gallery-dots]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.verticalMql = window.matchMedia('(min-width: 990px)');
        this.isVerticalThumbnailLayout = this.dataset.verticalThumbnails === 'true';
        this.hasGalleryControls = this.dataset.enableGalleryControls === 'true';
        this.onViewerSlideChanged = debounce(this.onSlideChanged.bind(this), 500);
        this.viewerResizeObserver = null;

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', this.onViewerSlideChanged);
        }

        if (this.isVerticalThumbnailLayout) {
          this.syncVerticalThumbnailHeight();
          if (this.elements.viewer) {
            this.viewerResizeObserver = new ResizeObserver(() => this.syncVerticalThumbnailHeight());
            this.viewerResizeObserver.observe(this.elements.viewer);
          }
          this.verticalMql.addEventListener?.('change', () => this.syncVerticalThumbnailHeight());
        }

        if (this.hasGalleryControls) {
          this.bindGalleryControls();
          this.rebuildDots();
        }

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        this.syncActiveMediaState(event.detail.currentElement.dataset.mediaId);
        this.updateGalleryControls(event.detail.currentElement.dataset.mediaId);
        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.syncActiveMediaState(activeMedia.dataset.mediaId);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          if (this.hasGalleryControls) this.rebuildDots();
        }
        
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateGalleryControls(mediaId);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.isDesktopVerticalThumbnails()) {
          this.scrollVerticalThumbnailIntoView(thumbnail);
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      bindGalleryControls() {
        if (!this.elements.controls) return;

        this.elements.controls.querySelectorAll('[data-gallery-control]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            const direction = button.dataset.galleryControl === 'next' ? 1 : -1;
            this.goToAdjacentMedia(direction);
          });
        });
      }

      rebuildDots() {
        if (!this.elements.dots) return;

        const mediaItems = this.getMediaItems();
        this.elements.dots.innerHTML = '';

        mediaItems.forEach((item, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'slider-counter__link slider-counter__link--dots link';
          dot.dataset.target = item.dataset.mediaId;
          dot.setAttribute('aria-label', `${index + 1}`);
          dot.innerHTML = '<span class="dot"></span>';
          dot.addEventListener('click', (event) => {
            event.preventDefault();
            this.setActiveMedia(item.dataset.mediaId, false);
          });
          this.elements.dots.appendChild(dot);
        });

        this.elements.controls?.toggleAttribute('hidden', mediaItems.length <= 1);
        this.updateGalleryControls(this.getCurrentMediaId());
      }

      updateGalleryControls(activeMediaId) {
        if (!this.hasGalleryControls || !this.elements.controls) return;

        const currentMediaId = activeMediaId || this.getCurrentMediaId();
        const mediaItems = this.getMediaItems();
        const currentIndex = mediaItems.findIndex((item) => item.dataset.mediaId === currentMediaId);

        this.elements.dots?.querySelectorAll('[data-target]').forEach((dot) => {
          const isActive = dot.dataset.target === currentMediaId;
          dot.classList.toggle('slider-counter__link--active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

        this.elements.controls
          .querySelector('[data-gallery-control="previous"]')
          ?.toggleAttribute('disabled', currentIndex <= 0);
        this.elements.controls
          .querySelector('[data-gallery-control="next"]')
          ?.toggleAttribute('disabled', currentIndex === -1 || currentIndex >= mediaItems.length - 1);
      }

      goToAdjacentMedia(direction) {
        const mediaItems = this.getMediaItems();
        const currentMediaId = this.getCurrentMediaId();
        const currentIndex = mediaItems.findIndex((item) => item.dataset.mediaId === currentMediaId);
        const targetIndex = currentIndex + direction;

        if (targetIndex < 0 || targetIndex >= mediaItems.length) return;
        this.setActiveMedia(mediaItems[targetIndex].dataset.mediaId, false);
      }

      getMediaItems() {
        return Array.from(this.elements.viewer?.querySelectorAll('.product__media-item[data-media-id]') || []);
      }

      getCurrentMediaId() {
        const activeMedia = this.elements.viewer?.querySelector('.is-active[data-media-id]');
        if (activeMedia) return activeMedia.dataset.mediaId;

        if (this.elements.viewer?.currentPage && this.elements.viewer.sliderItemsToShow?.length) {
          return this.elements.viewer.sliderItemsToShow[this.elements.viewer.currentPage - 1]?.dataset.mediaId;
        }

        return this.getMediaItems()[0]?.dataset.mediaId;
      }

      syncActiveMediaState(mediaId) {
        this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element.dataset.mediaId === mediaId);
        });
      }

      isDesktopVerticalThumbnails() {
        return this.isVerticalThumbnailLayout && this.verticalMql.matches && this.elements.thumbnails?.classList.contains('thumbnail-slider--vertical');
      }

      scrollVerticalThumbnailIntoView(thumbnail) {
        const thumbnailList = this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails"]');
        if (!thumbnailList) return;

        const listTop = thumbnailList.scrollTop;
        const listBottom = listTop + thumbnailList.clientHeight;
        const thumbTop = thumbnail.offsetTop;
        const thumbBottom = thumbTop + thumbnail.clientHeight;

        if (thumbTop >= listTop && thumbBottom <= listBottom) return;

        thumbnailList.scrollTo({
          top: Math.max(thumbnail.offsetTop - 8, 0),
          behavior: 'smooth',
        });
      }

      syncVerticalThumbnailHeight() {
        if (!this.isVerticalThumbnailLayout || !this.elements.viewer || !this.elements.thumbnails) return;

        if (!this.verticalMql.matches) {
          this.style.removeProperty('--gallery-viewer-height');
          return;
        }

        const viewerHeight = this.elements.viewer.getBoundingClientRect().height;
        if (!viewerHeight) return;

        this.style.setProperty('--gallery-viewer-height', `${viewerHeight}px`);
      }
    }
  );
}
